import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  Assignment,
  AssignmentType,
} from '../../../../models/assignment.school.model';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolAssignmentService } from '../../../../services/assignment.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as assignmentActions from '../../../../state/actions/assignment.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeLessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-add-assignment',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnSubmitComponent,
    S3DocumentUploadComponent,
  ],
  templateUrl: './add-assignment.component.html',
  styleUrl: './add-assignment.component.css',
})
export class AddAssignmentComponent implements OnInit, OnDestroy {
  @ViewChild('uploader') uploader: S3DocumentUploadComponent | undefined;

  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  assignmentService = inject(SchoolAssignmentService);
  toastr: ToastrService = inject(ToastrService);
  pageForm: FormGroup;
  private dialog: MatDialog = inject(MatDialog);

  uuid = uuidv4();

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.snapshot.paramMap.get('facultyId');

  group: Group | undefined;
  grade: Grade | null | undefined;
  faculty: SchoolMember | null | undefined;
  lessons: GradeLesson[] = [];
  loading$: Observable<boolean> = of(false);

  user: UserModel | null = null;

  preSignedUrlEndpoint: string = environment.AWS_SCHOOL_ASSIGNMENTS_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  assignmentType = AssignmentType;
  assignmentTypes = Object.values(AssignmentType);

  exit$: Subject<void> = new Subject();

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
      this.facultyId = params.get('facultyId');
    });

    this.pageForm = new FormGroup({
      endDate: new FormControl(
        dayjs().add(7, 'days').format('YYYY-MM-DDTHH:mm'),
        [Validators.required],
      ),
      lessonId: new FormControl(),
      lessonName: new FormControl(),
      type: new FormControl(AssignmentType.pdf),
    });

    this.loading$ = this.store.select(gradeLessonSelector.loading);

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.metadata.academicYear = group?.academicYear;
      });
  }

  ngOnInit(): void {
    this.store
      .select(memberSelector.getSchoolMemberById(this.facultyId, this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((teacher) => {
        this.faculty = teacher;

        if (!teacher?.gradeId) return;

        this.store
          .select(
            gradeLessonSelector.getGradeLessonsByGradeId({
              gradeId: teacher.gradeId,
              groupId: this.groupId!,
              academicYear: this.group?.academicYear,
              lessonStatus: [GradeLessonStatus.open, GradeLessonStatus.closed],
            }),
          )
          .pipe(takeUntil(this.exit$))
          .subscribe((lessons) => {
            this.lessons = lessons;
          });

        this.store
          .select(
            gradeSelector.getGradeById({
              gradeId: teacher.gradeId,
              groupId: this.groupId!,
              academicYear: this.group?.academicYear ?? null,
            }),
          )
          .pipe(takeUntil(this.exit$))
          .subscribe((grade) => {
            this.grade = grade;
          });
      });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.setLesson(undefined);
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onChangeLesson(event: any): void {
    const value = event.target.value;
    this.setLesson(value);
  }

  setLesson(value: string | undefined) {
    const lesson = this.lessons?.find((f) => f.id === value) ?? undefined;
    this.pageForm.patchValue({
      lessonId:
        lesson?.id ?? (this.lessons?.length > 0 ? this.lessons?.[0].id : null),
    });
    this.pageForm.patchValue({
      lessonName:
        lesson?.lessonName ??
        (this.lessons?.length > 0 ? this.lessons?.[0].lessonName : null),
    });
  }

  onChangeType(event: any): void {
    const value = event.target.value;
    this.pageForm.patchValue({ type: value });
  }

  async create() {
    if (
      this.pageForm.invalid ||
      !this.group ||
      !this.grade?.id ||
      (this.pageForm.get('type')?.value === AssignmentType.pdf &&
        !this.uploader?.getIsAttached())
    ) {
      return;
    }

    this.loading$ = of(true);

    this.uploader?.setInputParams({
      groupId: this.groupId,
      gradeId: this.grade.id,
      academicYear: this.metadata.academicYear ?? this.group?.academicYear,
      assignmentId: this.uuid,
    });
    this.uploader?.setMetadata({
      groupId: this.groupId,
      gradeId: this.grade.id,
      academicYear: this.metadata.academicYear ?? this.group?.academicYear,
      assignmentId: this.uuid,
    });

    if (this.pageForm.get('type')?.value === AssignmentType.pdf) {
      this.uploader
        ?.upload()
        ?.then((result) => {
          if (result?.successful) {
            this.createDatabaseEntry(
              this.uuid,
              result.successful[0]?.response?.uploadURL,
            );
          } else {
            this.loading$ = of(false);
            this.toastr.error(
              `Failed to upload document. ${result?.failed}`,
              'Document Upload Error',
            );
          }
        })
        .catch((error) => {
          this.loading$ = of(false);
          this.onShowAlert(error as AppExceptionModel);
        });
    } else {
      await this.createDatabaseEntry(this.uuid);
      this.loading$ = of(false);
    }
  }

  async createDatabaseEntry(id: string, url?: string | undefined) {
    // console.log(
    //   `AddAssignmentComponent :: createDatabaseEntry: id: ${id}, Url: ${JSON.stringify(
    //     url
    //   )}`
    // );

    try {
      let data = {
        ...this.pageForm.value,
        endDate: Timestamp.fromDate(
          new Date(this.pageForm.get('endDate')?.value),
        ),
        groupId: this.group?.id,
        gradeId: this.grade?.id,
        academicYear: this.group?.academicYear,
        createdBy: this.user?.name,
        createdById: this.user?.uid,
      };

      if (url)
        data.assignmentUrl = UtilsService.replaceDomain(
          url,
          this.cloudFrontDomain,
        );

      await this.assignmentService.create(
        this.groupId!,
        id,
        data as Assignment,
      );

      // console.log(
      //   `AddAssignmentComponent :: createDatabaseEntry: Created Assignment: ${JSON.stringify(
      //     data,
      //     null,
      //     2
      //   )}`
      // );

      this.store.dispatch(
        assignmentActions.createSuccess({
          assignment: {
            ...data,
            id: id,
            created: Timestamp.now(),
            modified: Timestamp.now(),
          } as Assignment,
        }),
      );
      this.router.navigate(['..'], {
        relativeTo: this.route,
        fragment: 'assignments',
      });
    } catch (error) {
      this.onShowAlert(error as AppExceptionModel);
      this.loading$ = of(false);
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
